/*
  Peter Augustus | scroll reveal for the pa-home sections
  13 Oct 2026 | Theme 138814587088

  A section opts in with data-pa-anim="reveal". Inside it:
  - data-reveal="heading"  lines rise out of a mask (Osmo Masked Text Reveal:
                           lines, 0.8s, stagger 0.08, Osmo's ease)
  - data-reveal="fade"     fades up
  - data-reveal="tile"     wipes up from the bottom while its [data-reveal-media]
                           eases out of a 1.2 zoom; its [data-reveal-text] fade up after
  - data-reveal-rows       a table built from cells marked data-reveal-row="<n>";
                           the cells of each row fade up together, row after row
                           (0.8s each, 0.15s apart; slowed from 0.6 and 0.1 on Calvin's ask), when the table itself reaches 85% of the
                           viewport. Columns can sit apart in the DOM (Know Your Beef).
  - data-reveal-cards      a list whose [data-reveal-card] items come in one after
                           another (0.9s each, 0.12s apart) when the list reaches
                           85% of the viewport: rising on desktop, sliding in from
                           the right on mobile, where the list is a swipe row that
                           would clip a rise, and on every width when the list is
                           data-reveal-cards="x" (UGC reels). The children of a
                           card's [data-reveal-stars] then pop in one by one (Reviews).
  - data-reveal-lines      a list whose [data-reveal-rule] lines draw across from the
                           left one after another (1s each, 0.15s apart) while each
                           [data-reveal-line] fades up under its line, when the list
                           reaches 85% of the viewport (From Our Stations pillars).
  - data-reveal-steps      a list whose [data-reveal-step] items light up one after
                           another, 0.5s apart: the [data-reveal-dot] pops in, the
                           [data-reveal-step-text] fades up and the [data-reveal-rail]
                           line grows down to the next dot, when the list reaches 85%
                           of the viewport (Delivery ticket).
  - data-reveal-bg         a photo behind the copy that settles from a 1.08 zoom as
                           the section reveals. Desktop only: on mobile the photo
                           sits above the copy and stays still (Visit Us).
  Everything else plays once, in page order, when the section's top reaches 80%
  of the viewport. Inline styles are cleared at the end so hover effects and
  resizing go back to the stylesheet. Reduced motion: nothing is hidden.
  A heading fires "pa:revealed" once its lines are plain text again, so a
  script that writes into a heading (the live REVIEWS.io score) can write again.
*/
(() => {
  if (!window.PAHome || !window.gsap) return;
  const EASE = window.CustomEase ? 'osmo-ease' : 'expo.out';

  window.PAHome.register('reveal', (section, { reduceMotion, isMobile }, context) => {
    if (reduceMotion) return undefined;

    const headings = [...section.querySelectorAll('[data-reveal="heading"]')];
    const fades = [...section.querySelectorAll('[data-reveal="fade"]')];
    const tiles = [...section.querySelectorAll('[data-reveal="tile"]')];
    const tables = [...section.querySelectorAll('[data-reveal-rows]')].map((box) => {
      const byRow = new Map();
      box.querySelectorAll('[data-reveal-row]').forEach((cell) => {
        const n = Number(cell.dataset.revealRow) || 0;
        if (!byRow.has(n)) byRow.set(n, []);
        byRow.get(n).push(cell);
      });
      return { box, rows: [...byRow.keys()].sort((a, b) => a - b).map((n) => byRow.get(n)) };
    });
    // Cards slide in from the right in a swipe row (mobile, or any list set to data-reveal-cards="x"), else rise
    const decks = [...section.querySelectorAll('[data-reveal-cards]')]
      .map((box) => ({
        box,
        cards: [...box.querySelectorAll('[data-reveal-card]')],
        from: isMobile || box.dataset.revealCards === 'x' ? { x: 48 } : { y: 32 }
      }))
      .filter(({ cards }) => cards.length);
    const starsOf = (card) => [...card.querySelectorAll('[data-reveal-stars] > *')];
    const lineLists = [...section.querySelectorAll('[data-reveal-lines]')]
      .map((box) => ({ box, rules: [...box.querySelectorAll('[data-reveal-rule]')], lines: [...box.querySelectorAll('[data-reveal-line]')] }))
      .filter(({ rules, lines }) => rules.length || lines.length);
    const stepLists = [...section.querySelectorAll('[data-reveal-steps]')]
      .map((box) => ({ box, steps: [...box.querySelectorAll('[data-reveal-step]')] }))
      .filter(({ steps }) => steps.length);
    const partsOf = (step, name) => [...step.querySelectorAll(`[data-reveal-${name}]`)];
    const bgs = isMobile ? [] : [...section.querySelectorAll('[data-reveal-bg]')];
    if (!headings.length && !fades.length && !tiles.length && !tables.length && !decks.length && !lineLists.length && !stepLists.length && !bgs.length) return undefined;

    // Hidden until the section scrolls in (sections start below the fold, so nothing flashes)
    gsap.set([...headings, ...fades], { autoAlpha: 0 });
    gsap.set(tiles, { clipPath: 'inset(100% 0% 0% 0%)' });
    gsap.set(bgs, { scale: 1.08 });
    tiles.forEach((tile) => gsap.set(tile.querySelectorAll('[data-reveal-text]'), { autoAlpha: 0, y: 16 }));
    tables.forEach(({ rows }) => gsap.set(rows.flat(), { autoAlpha: 0, y: 16 }));
    decks.forEach(({ cards, from }) => {
      gsap.set(cards, { autoAlpha: 0, ...from });
      gsap.set(cards.flatMap(starsOf), { autoAlpha: 0, scale: 0.4 });
    });
    lineLists.forEach(({ rules, lines }) => {
      gsap.set(rules, { scaleX: 0, transformOrigin: '0% 50%' });
      gsap.set(lines, { autoAlpha: 0, y: 12 });
    });
    stepLists.forEach(({ steps }) => steps.forEach((step) => {
      gsap.set(partsOf(step, 'dot'), { scale: 0 });
      gsap.set(partsOf(step, 'rail'), { scaleY: 0, transformOrigin: '50% 0%' });
      gsap.set(partsOf(step, 'step-text'), { autoAlpha: 0, y: 8 });
    }));

    context.add('play', () => {
      const tl = gsap.timeline();
      const cleared = [];

      headings.forEach((heading) => {
        gsap.set(heading, { autoAlpha: 1 });
        if (!window.SplitText) return;
        const split = window.SplitText.create(heading, { type: 'lines', mask: 'lines' });
        tl.fromTo(split.lines, { yPercent: 110 }, {
          yPercent: 0,
          duration: 0.8,
          stagger: 0.08,
          ease: EASE,
          onComplete: () => {
            split.revert();
            heading.dispatchEvent(new CustomEvent('pa:revealed', { bubbles: true }));
          }
        }, 0);
      });

      if (fades.length) {
        tl.fromTo(fades, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.08, ease: 'power3.out' }, 0.15);
        cleared.push(...fades);
      }

      if (bgs.length) {
        tl.to(bgs, { scale: 1, duration: 1.8, ease: 'expo.out' }, 0);
        cleared.push(...bgs);
      }

      tiles.forEach((tile, i) => {
        const at = 0.2 + i * 0.12;
        tl.to(tile, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.1, ease: EASE }, at);
        const media = tile.querySelector('[data-reveal-media]');
        if (media) {
          tl.fromTo(media, { scale: 1.2 }, { scale: 1, duration: 1.4, ease: 'expo.out' }, at);
          cleared.push(media);
        }
        const texts = [...tile.querySelectorAll('[data-reveal-text]')];
        if (texts.length) {
          tl.to(texts, { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.06, ease: 'power3.out' }, at + 0.45);
          cleared.push(...texts);
        }
        cleared.push(tile);
      });

      tl.add(() => gsap.set(cleared, { clearProps: 'opacity,visibility,transform,clipPath' }));
    });

    // A table builds row by row once the table itself is in view
    context.add('playRows', (rows) => {
      const tl = gsap.timeline();
      rows.forEach((cells, i) => tl.to(cells, { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power3.out' }, i * 0.15));
      tl.add(() => gsap.set(rows.flat(), { clearProps: 'opacity,visibility,transform' }));
    });

    // Cards come in one after another, then each card's stars pop in
    context.add('playCards', (cards) => {
      const tl = gsap.timeline();
      cards.forEach((card, i) => {
        const at = i * 0.12;
        tl.to(card, { autoAlpha: 1, x: 0, y: 0, duration: 0.9, ease: 'power3.out' }, at);
        const stars = starsOf(card);
        if (stars.length) tl.to(stars, { autoAlpha: 1, scale: 1, duration: 0.45, stagger: 0.06, ease: 'back.out(2.5)' }, at + 0.35);
      });
      tl.add(() => gsap.set([...cards, ...cards.flatMap(starsOf)], { clearProps: 'opacity,visibility,transform' }));
    });

    // Rules draw across one after another, each row's text rising under its rule
    context.add('playLines', ({ rules, lines }) => {
      const tl = gsap.timeline();
      rules.forEach((rule, i) => tl.to(rule, { scaleX: 1, duration: 1, ease: EASE }, i * 0.15));
      lines.forEach((line, i) => tl.to(line, { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out' }, 0.2 + i * 0.15));
      tl.add(() => gsap.set([...rules, ...lines], { clearProps: 'opacity,visibility,transform,transformOrigin' }));
    });

    // Steps light up down the rail: dot, text, then the line to the next dot
    context.add('playSteps', (steps) => {
      const tl = gsap.timeline();
      const parts = [];
      steps.forEach((step, i) => {
        const at = i * 0.5;
        const [dots, rails, texts] = ['dot', 'rail', 'step-text'].map((name) => partsOf(step, name));
        tl.to(dots, { scale: 1, duration: 0.35, ease: 'back.out(2.5)' }, at);
        tl.to(texts, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power3.out' }, at + 0.05);
        tl.to(rails, { scaleY: 1, duration: 0.45, ease: 'power1.inOut' }, at + 0.15);
        parts.push(...dots, ...rails, ...texts);
      });
      tl.add(() => gsap.set(parts, { clearProps: 'opacity,visibility,transform,transformOrigin' }));
    });

    ScrollTrigger.create({ trigger: section, start: 'top 80%', once: true, onEnter: () => context.play() });
    tables.forEach(({ box, rows }) => {
      ScrollTrigger.create({ trigger: box, start: 'top 85%', once: true, onEnter: () => context.playRows(rows) });
    });
    decks.forEach(({ box, cards }) => {
      ScrollTrigger.create({ trigger: box, start: 'top 85%', once: true, onEnter: () => context.playCards(cards) });
    });
    lineLists.forEach((list) => {
      ScrollTrigger.create({ trigger: list.box, start: 'top 85%', once: true, onEnter: () => context.playLines(list) });
    });
    stepLists.forEach(({ box, steps }) => {
      ScrollTrigger.create({ trigger: box, start: 'top 85%', once: true, onEnter: () => context.playSteps(steps) });
    });
    return undefined;
  });
})();
